/* Copyright (c) 2020-present CloudIO Inc */

import { IoLookups, Query } from '@cloudio-saas/datasource-types';
import { useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LookupAttributeChooserProp } from 'componentregistry';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import React, { memo } from 'react';
import { isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

export const dsFilter: () => Query<IoLookups> = () => ({
  filter: [],
  limit: 5000,
  offset: 0,
  projection: {
    lookupUid: 1,
    isNumber: 1,
  },
});

function LookupAttributeChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = 'icon',
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<LookupAttributeChooserProp, string>) {
  let lookupAttributes: Option[] = [];

  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (code: string) => {
    if (code !== value) {
      onChange(property, code ? code : undefined);
    }
  };
  lookupAttributes = [
    { value: 'icon', label: 'Icon' },
    { value: 'color', label: 'Color' },
    { value: 'description', label: 'Description' },
    { value: 'startDate', label: 'Start Date' },
    { value: 'endDate', label: 'End Date' },
  ];

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <FormControl
      data-testid={property}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        value={value}
        options={lookupAttributes}
        onChange={handleChange}
        label={def.label}
        disabled={isViewOnlyUser}
        placeholder={'Choose a Lookup Attribute'}
        variant="standard"
        required={required}
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

export default memo(LookupAttributeChooserProperty);
